import React, { useState } from 'react';
import { Grid, Typography, Divider } from '@mui/material';
import GridContainer from 'components/GridContainer';
import Spacing from 'components/Spacing';
import Heading from 'components/Heading';
import Button from 'components/Button';
import ButtonGroup from 'components/ButtonGroup';
import api from 'lib/trpcClient';
import { PartnerTransfersFormValues } from '../hooks/usePartnerTransfersForm';
import {
  Controller,
  useFieldArray,
  Control,
  UseFormSetValue,
  useFormContext,
} from 'react-hook-form';
import AppSimpleSelect from 'components/FormElements/AppSimpleSelect';
import FieldRow from 'components/FormElements/FieldRow';
import {
  CalendarDay,
  dateToLabel,
  numberToCurrencyString,
} from 'further-ui/utils';
import RecordView from 'components/RecordView';
import { CurrencyCode } from 'further-types/lp/vehicle';
import AppDatePicker from 'components/FormElements/AppDatePicker';
import { useDisclosure } from 'further-ui/hooks';
import DataLossWarningModal from 'components/DataLossWarningModal';
import AppNumberInput from 'components/FormElements/AppNumberInput';
import { round, sumBy } from 'lodash';
import { useNotification } from 'hooks/ui/useNotification';
import { AccountType } from 'further-types/investor';
import { PartnerTransferSection } from 'further-types/lp/partner-transfer';

type Props = {
  onNext: (partnerTransferId?: string) => void;
  isSubmitting?: boolean;
  baseCurrency: CurrencyCode;
  vehicleId: string;
  id?: string;
};

const AddTransfer: React.FC<Props> = ({
  onNext,
  isSubmitting = false,
  baseCurrency,
  vehicleId,
  id,
}) => {
  const formMethods = useFormContext<PartnerTransfersFormValues>();
  const selectedTrasferrerCommitmentId = formMethods.watch(
    'trasferrorCommitmentId',
  );

  const notification = useNotification();
  const utils = api.useUtils();

  const { fields, append, remove, replace } = useFieldArray({
    control: formMethods.control,
    name: 'transferees',
  });

  const transferrerChangeDisclosure = useDisclosure();
  const [pendingTransferrerCommitmentId, setPendingTransferrerCommitmentId] =
    useState<string>('');
  const [previousTransferrerCommitmentId, setPreviousTransferrerCommitmentId] =
    useState<string>('');

  const [commitments] = api.commitments.list.useSuspenseQuery({
    vehicleId,
    onlyEqualised: true,
    order: 'desc',
    page: 1,
    rowsPerPage: 1000,
    orderBy: 'commitmentDate',
  });

  const [investors] = api.lpInvestor.getAll.useSuspenseQuery();

  const createTransferMutation = api.partnerTransfers.create.useMutation({
    onSuccess: (data) => {
      notification.success('Partner transfer created successfully');
      utils.partnerTransfers.getById.invalidate();
      utils.partnerTransfers.getLinkedCapitalActions.invalidate();
      utils.partnerTransfers.getGeneralCapitalActions.invalidate();
      utils.partnerTransfers.getLinkedCapitalActionsTransactions.invalidate();
      utils.partnerTransfers.getGeneralCapitalActionsTransactions.invalidate();

      if (data?.partnerTransfer) {
        formMethods.setValue('partnerTransferId', data.partnerTransfer.id);
        formMethods.setValue(
          'currentSection',
          PartnerTransferSection.Investments,
        );
        onNext(data.partnerTransfer.id);
      }
    },
    onError: (err) => {
      notification.error(
        err?.data?.cause ?? err?.message ?? 'Something went wrong.',
      );
    },
  });

  const updateTransferMutation = api.partnerTransfers.update.useMutation({
    onSuccess: (_data) => {
      notification.success('Partner transfer updated successfully');
      utils.partnerTransfers.getById.invalidate();
      utils.partnerTransfers.getLinkedCapitalActions.invalidate();
      utils.partnerTransfers.getGeneralCapitalActions.invalidate();
      utils.partnerTransfers.getLinkedCapitalActionsTransactions.invalidate();
      utils.partnerTransfers.getGeneralCapitalActionsTransactions.invalidate();

      const partnerTransferId = formMethods.getValues('partnerTransferId');

      if (partnerTransferId) {
        onNext(partnerTransferId);
      }
    },
    onError: (err) => {
      notification.error(
        err?.data?.cause ?? err?.message ?? 'Something went wrong.',
      );
    },
  });

  const validateTransferMutation = api.partnerTransfers.validate.useMutation({
    onSuccess: (data) => {
      if (!data.isValid && data.errors.length > 0) {
        formMethods.setError('transferDate', {
          type: 'custom',
          message: data.errors.join('\n'),
        });
      } else {
        formMethods.clearErrors('transferDate');
      }
    },
  });

  const selectedCommitment = commitments.results.find(
    (commitment) => commitment.id === selectedTrasferrerCommitmentId,
  );

  const handleDateChange = (date: Date | null) => {
    if (date) {
      const calendarDay = new CalendarDay(date);
      formMethods.setValue('transferDate', calendarDay);
      formMethods.setValue('capitalActionTransactions', {});

      if (selectedTrasferrerCommitmentId) {
        const partnerTransferId =
          id ?? formMethods.getValues('partnerTransferId');
        validateTransferMutation.mutate({
          vehicleId,
          commitmentId: selectedTrasferrerCommitmentId,
          transferDate: calendarDay,
          partnerTransferId,
        });
      }
    } else {
      formMethods.resetField('transferDate');
      formMethods.clearErrors('transferDate');
    }
  };

  const getAvailableInvestorsForTransferee = (index: number) => {
    const selectedInvestorId = formMethods.watch(
      `transferees.${index}.investorId`,
    );

    const alreadySelectedInvestorIds = fields
      .map((_, idx) => {
        if (idx === index) return null;
        return formMethods.watch(`transferees.${idx}.investorId`);
      })
      .filter(Boolean) as Array<string>;

    const availableInvestors = investors.filter((investor) => {
      return (
        investor.id === selectedInvestorId ||
        !alreadySelectedInvestorIds.includes(investor.id)
      );
    });

    const availableInvestorsWithName = availableInvestors.map((investor) => ({
      id: investor.id,
      investorLabel:
        investor.accountType === AccountType.Individual
          ? `${investor.firstName} ${investor.lastName}`
          : investor.registeredName,
    }));

    return availableInvestorsWithName;
  };

  const handleTransfereeInvestorChange = (index: number, value: string) => {
    const selectedInvestor = investors.find(
      (investor) => investor.id === value,
    );
    if (selectedInvestor) {
      const label =
        selectedInvestor.accountType === AccountType.Individual
          ? `${selectedInvestor.firstName} ${selectedInvestor.lastName}`
          : selectedInvestor.registeredName;
      formMethods.setValue(`transferees.${index}.investorLabel`, label);
      formMethods.setValue('capitalActionTransactions', {});
    }
  };

  const addTransferee = () => {
    append({
      investorId: '',
      investorLabel: '',
      amountLcy: 0,
      percentage: 0,
    });
    formMethods.setValue('capitalActionTransactions', {});
  };

  const removeTransferee = (index: number) => {
    remove(index);
    formMethods.setValue('capitalActionTransactions', {});
  };

  const hasTransfereesData = () => {
    return fields.some((_, index) => {
      const investorId = formMethods.watch(`transferees.${index}.investorId`);
      return investorId && investorId !== '';
    });
  };

  const resetTransferees = () => {
    replace([
      {
        investorId: '',
        investorLabel: '',
        amountLcy: 0,
        percentage: 0,
      },
    ]);
  };

  const handleTransferrerInvestorChange = (value: string) => {
    const hasData = hasTransfereesData();

    if (hasData && selectedTrasferrerCommitmentId) {
      setPendingTransferrerCommitmentId(value);
      setPreviousTransferrerCommitmentId(selectedTrasferrerCommitmentId);
      transferrerChangeDisclosure.onOpen();
    } else {
      formMethods.setValue('trasferrorCommitmentId', value);
      formMethods.resetField('transferDate');
      formMethods.clearErrors('transferDate');
      formMethods.setValue('capitalActionTransactions', {});
    }
  };

  const handleConfirmTransferrerChange = () => {
    formMethods.setValue(
      'trasferrorCommitmentId',
      pendingTransferrerCommitmentId,
    );
    formMethods.resetField('transferDate');
    formMethods.clearErrors('transferDate');
    formMethods.setValue('capitalActionTransactions', {});
    resetTransferees();
    transferrerChangeDisclosure.onClose();
  };

  const handleCancelTransferrerChange = () => {
    formMethods.setValue(
      'trasferrorCommitmentId',
      previousTransferrerCommitmentId,
    );
    setPendingTransferrerCommitmentId('');
    setPreviousTransferrerCommitmentId('');
    transferrerChangeDisclosure.onClose();
  };

  const handleNext = async (data: PartnerTransfersFormValues) => {
    const partnerTransferId = id ?? formMethods.getValues('partnerTransferId');
    const validationResult = await validateTransferMutation.mutateAsync({
      vehicleId,
      commitmentId: data.trasferrorCommitmentId,
      transferDate: data.transferDate,
      partnerTransferId,
    });

    if (!validationResult.isValid) {
      return;
    }

    const totalTransferredAmountLcy = sumBy(data.transferees, 'amountLcy');

    if (
      selectedCommitment &&
      totalTransferredAmountLcy > selectedCommitment.totalCommitmentLcy
    ) {
      notification.error(
        'The total to be transferred is in excess of the commitment amount - please adjust the proposed transfer amounts.  ',
      );
      return;
    }

    if (partnerTransferId) {
      // Update existing partner transfer
      await updateTransferMutation.mutateAsync({
        partnerTransferId,
        vehicleId,
        transferorCommitmentId: data.trasferrorCommitmentId,
        transferDate: data.transferDate,
        currentSection: PartnerTransferSection.PartnerTransfer,
        actions: formMethods.getValues('actions'),
        transferees: data.transferees.map((transferee) => ({
          investorId: transferee.investorId,
          amountLcy: transferee.amountLcy,
          percentage: transferee.percentage,
        })),
      });
    } else {
      // Create new partner transfer
      await createTransferMutation.mutateAsync({
        vehicleId,
        transferorCommitmentId: data.trasferrorCommitmentId,
        transferDate: data.transferDate,
        transferees: data.transferees,
      });
    }

    formMethods.clearErrors('transferDate');
  };

  return (
    <GridContainer>
      <Grid size={{ xs: 12, lg: 8 }}>
        <Spacing size="md">
          <Heading variant="h2" noMargin>
            Process partner transfer
          </Heading>

          <Grid size={{ xs: 12, lg: 6 }}>
            <FieldRow title="Transfer from*" centerTitle>
              <Controller
                control={formMethods.control}
                name="trasferrorCommitmentId"
                render={({ field, fieldState: { error } }) => (
                  <AppSimpleSelect
                    fullWidth
                    placeholder="Select a commitment"
                    options={commitments.results.map((commitment) => ({
                      label: `${commitment.investorName} - ${dateToLabel(
                        commitment.commitmentDate,
                      )}`,
                      value: commitment.id,
                    }))}
                    value={field.value}
                    onChange={(e) => {
                      const value = e.target.value as string;
                      handleTransferrerInvestorChange(value);
                    }}
                    error={!!error}
                    errorMessage={error?.message}
                  />
                )}
              />
            </FieldRow>
          </Grid>

          {selectedCommitment && (
            <Grid size={{ xs: 12, lg: 6 }}>
              <Spacing size="md">
                <CommitmentDetails
                  commitmentDate={selectedCommitment.commitmentDate}
                  capitalCommitmentLcy={selectedCommitment.capitalCommitmentLcy}
                  loanCommitmentLcy={selectedCommitment.loanCommitmentLcy}
                  baseCurrency={baseCurrency}
                  totalCommitmentLcy={selectedCommitment.totalCommitmentLcy}
                />

                <FieldRow title="Date of transfer*" centerTitle>
                  <Controller
                    control={formMethods.control}
                    name="transferDate"
                    render={({ field, fieldState: { error } }) => (
                      <AppDatePicker
                        value={field.value?.toUTCDate()}
                        onChange={handleDateChange}
                        error={!!error}
                        helperText={error?.message}
                        fullWidth
                      />
                    )}
                  />
                </FieldRow>
                {fields.map((field, index) => (
                  <TransfereeField
                    key={field.id}
                    selectedCommitment={{
                      investorLabel: selectedCommitment.investorName,
                      capitalCommitmentLcy:
                        selectedCommitment.capitalCommitmentLcy,
                      loanCommitmentLcy: selectedCommitment.loanCommitmentLcy,
                    }}
                    baseCurrency={baseCurrency}
                    totalCommitmentLcy={selectedCommitment.totalCommitmentLcy}
                    index={index}
                    availableInvestors={getAvailableInvestorsForTransferee(
                      index,
                    )}
                    setValue={formMethods.setValue}
                    onInvestorChange={handleTransfereeInvestorChange}
                    onAdd={addTransferee}
                    onRemove={() => removeTransferee(index)}
                    isLast={index === fields.length - 1}
                    isOnlyTransferee={fields.length === 1}
                    control={formMethods.control}
                  />
                ))}
              </Spacing>
            </Grid>
          )}

          <ButtonGroup>
            <Button
              variant="contained"
              disabled={!formMethods.formState.isValid}
              onClick={formMethods.handleSubmit(handleNext)}
              loading={isSubmitting || validateTransferMutation.isPending}
            >
              Save and continue
            </Button>
          </ButtonGroup>
        </Spacing>
      </Grid>
      <DataLossWarningModal
        disclosure={transferrerChangeDisclosure}
        onConfirm={handleConfirmTransferrerChange}
        onCancel={handleCancelTransferrerChange}
        message="Changing the transfer from commitment will reset all transferees. Do you wish to continue?"
      />
    </GridContainer>
  );
};

type TransfereeFieldProps = {
  index: number;
  availableInvestors: Array<{
    id: string;
    investorLabel: string;
  }>;
  onInvestorChange: (index: number, value: string) => void;
  onAdd: () => void;
  onRemove: () => void;
  setValue: UseFormSetValue<PartnerTransfersFormValues>;
  isLast: boolean;
  isOnlyTransferee: boolean;
  control: Control<PartnerTransfersFormValues>;
  selectedCommitment: {
    investorLabel: string;
    capitalCommitmentLcy: number;
    loanCommitmentLcy: number;
  };
  baseCurrency: CurrencyCode;
  totalCommitmentLcy: number;
};

const TransfereeField: React.FC<TransfereeFieldProps> = ({
  index,
  availableInvestors,
  onInvestorChange,
  onAdd,
  onRemove,
  isLast,
  isOnlyTransferee,
  selectedCommitment,
  baseCurrency,
  control,
  totalCommitmentLcy,
  setValue,
}) => {
  const removeDisclosure = useDisclosure();

  const handleAmountChange = (value: number) => {
    const newPercentage = (value / totalCommitmentLcy) * 100;
    setValue(`transferees.${index}.percentage`, round(newPercentage, 2));
    setValue('capitalActionTransactions', {});
  };

  const handlePercentageChange = (value: number) => {
    const newAmount = (value / 100) * totalCommitmentLcy;
    setValue(`transferees.${index}.amountLcy`, round(newAmount, 2));
    setValue('capitalActionTransactions', {});
  };

  return (
    <>
      {index > 0 && <Divider />}
      <FieldRow title={`Transferee ${index + 1}*`} centerTitle>
        <Controller
          control={control}
          name={`transferees.${index}.investorId`}
          render={({ field: controllerField }) => (
            <AppSimpleSelect
              fullWidth
              options={availableInvestors.map((investor) => ({
                label: investor.investorLabel,
                value: investor.id,
              }))}
              value={controllerField.value}
              onChange={(event) => {
                const selectedValue = event.target.value as string;
                controllerField.onChange(selectedValue);
                onInvestorChange(index, selectedValue);
              }}
              placeholder="Select a investor"
            />
          )}
        />
      </FieldRow>
      <FieldRow
        title={`Commitment to transfer to ${selectedCommitment?.investorLabel}*`}
        centerTitle
      >
        <Grid size={{ xs: 12, lg: 7 }}>
          <Controller
            control={control}
            name={`transferees.${index}.amountLcy`}
            render={({ field: controllerField }) => (
              <AppNumberInput
                fullWidth
                placeholder="Enter amount"
                currencyStartAdornment
                currency={baseCurrency}
                decimals={2}
                max={totalCommitmentLcy}
                min={0}
                value={controllerField.value}
                onChange={(value) => {
                  controllerField.onChange(value);
                  handleAmountChange(value);
                }}
              />
            )}
          />
        </Grid>
      </FieldRow>
      <FieldRow title="Percentage*" centerTitle>
        <Grid size={{ xs: 12, lg: 5 }}>
          <Controller
            control={control}
            name={`transferees.${index}.percentage`}
            render={({ field: controllerField }) => (
              <AppNumberInput
                fullWidth
                placeholder="Enter percentage"
                max={100}
                min={0}
                decimals={2}
                percentEndAdornment
                value={controllerField.value}
                onChange={(value) => {
                  controllerField.onChange(value);
                  handlePercentageChange(value);
                }}
              />
            )}
          />
        </Grid>
      </FieldRow>

      <GridContainer>
        <Grid size={{ xs: 6 }} display="flex" justifyContent="flex-start">
          {isLast && (
            <Button variant="text" onClick={onAdd} color="primary">
              Add additional transferee
            </Button>
          )}
        </Grid>
        <Grid size={{ xs: 6 }} display="flex" justifyContent="flex-end">
          {!isOnlyTransferee && (
            <Button
              variant="text"
              onClick={removeDisclosure.onOpen}
              color="error"
            >
              Remove transferee
            </Button>
          )}
        </Grid>
      </GridContainer>
      <DataLossWarningModal
        message="If you remove this transferee, all information you have input will be lost. Do you wish to continue?"
        disclosure={removeDisclosure}
        onConfirm={onRemove}
        onCancel={removeDisclosure.onClose}
      />
    </>
  );
};

type CommitmentDetailsProps = {
  commitmentDate: Date;
  totalCommitmentLcy: number;
  capitalCommitmentLcy: number;
  loanCommitmentLcy: number;
  baseCurrency: CurrencyCode;
};

const CommitmentDetails: React.FC<CommitmentDetailsProps> = ({
  commitmentDate,
  capitalCommitmentLcy,
  loanCommitmentLcy,
  baseCurrency,
  totalCommitmentLcy,
}) => {
  return (
    <RecordView size="full">
      <Spacing size="md">
        <FieldRow title="Commitment date" centerTitle>
          <Typography variant="body2">{dateToLabel(commitmentDate)}</Typography>
        </FieldRow>
        <FieldRow title="Capital contribution" centerTitle>
          <Typography variant="body2">
            {numberToCurrencyString(capitalCommitmentLcy, {
              currency: baseCurrency,
            })}
          </Typography>
        </FieldRow>
        <FieldRow title="Loan commitment" centerTitle>
          <Typography variant="body2">
            {numberToCurrencyString(loanCommitmentLcy, {
              currency: baseCurrency,
            })}
          </Typography>
        </FieldRow>
        <FieldRow title="Total commitment" centerTitle>
          <Typography variant="body2">
            {numberToCurrencyString(totalCommitmentLcy, {
              currency: baseCurrency,
            })}
          </Typography>
        </FieldRow>
      </Spacing>
    </RecordView>
  );
};

export default AddTransfer;
